import {
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_MATCH_LICENSE_NUMBER_VALIDATION,
  MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
} from "@/constants/common";
import { INVALID_MATCH_LICENSE_NUMBER } from "@/constants/invalids";
import { PATTERN } from "@/constants/pattern";
import { validator } from "@/utils/validator";
import { useMemo } from "react";

export const useGetMatchSchema = () => {
  const matchSchema = useMemo(() => {
    return validator.object().shape({
      matchName: validator.string().trim().emptyToNull().max(60).label("会社名").nameOnly().required(),
      matchNameRomaji: validator
        .string()
        .trim()
        .emptyToNull()
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .label("会社名（ローマ字）")
        .nullable()
        .notRequired()
        .romajiOnly(),
      matchLicenseNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .label("紹介事業許可番号")
        .max(MAX_MATCH_LICENSE_NUMBER_VALIDATION)
        .matches(PATTERN.MATCH_LICENSE_NUMBER, INVALID_MATCH_LICENSE_NUMBER),
      matchLicenseExpiryDate: validator.mixed().isDayjs().isSelection().nullable().notRequired().label("許可有効期限"),
      zipCode: validator
        .string()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .zipCode()
        .trim()
        .label("郵便番号")
        .notRequired()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      prefecture: validator.string().trim().emptyToNull().label("都道府県").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      city: validator.string().trim().emptyToNull().label("市区町村").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      address: validator.string().emptyToNull().nullable().label("町名丁目番地号等").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION).trim(),
      matchOffices: validator
        .array()
        .of(
          validator.object().shape({
            officeName: validator.string().trim().emptyToNull().label("事業所名").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
            fullName: validator.string().trim().emptyToNull().nameOnly().label("氏名").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
            trainingDate: validator.mixed().isDayjs().isSelection().nullable().notRequired().label("講習受講日"),
          }),
        )
        .test("first-office-name-required", function (value) {
          if (value && value.length > 0 && !value[0]?.officeName) {
            return this.createError({
              path: `${this.path}[0].officeName`,
              message: "「事業所名」を入力してください。",
            });
          }
          return true;
        })
        .label("職業紹介責任者情報")
        .notRequired(),
      representativeName: validator
        .string()
        .trim()
        .nameOnly()
        .emptyToNull()
        .label("代表者氏名")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable()
        .test("is-valid-name", "「代表者氏名」は日本語または英字のみ入力できます。", function (value) {
          if (!value) return true;
          return /^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\u3400-\u4DBF\u3005 A-Za-zＡ-Ｚａ-ｚ\s]+$/.test(value);
        })
        .autoTransformToFullWidth(),
      representativePosition: validator
        .string()
        .trim()
        .emptyToNull()
        .label("役職")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable()
        .autoTransformToFullWidth(),
      phoneNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .isTelNumber()
        .label("電話番号")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable(),
      jobInputRule: validator.string().trim().emptyToNull().label("求人の入力ルール").max(MAX_TEXT_INPUT_VALIDATION).nullable().notRequired(),
      talentInputRule: validator.string().trim().emptyToNull().label("人材の入力ルール").max(MAX_TEXT_INPUT_VALIDATION).nullable().notRequired(),
      talentCharacteristicMemoTemplate: validator
        .string()
        .trim()
        .emptyToNull()
        .label("人材の特性メモ雛形")
        .max(MAX_TEXT_INPUT_VALIDATION)
        .nullable()
        .notRequired(),
      talentInterviewEvaluationTemplate: validator
        .string()
        .trim()
        .emptyToNull()
        .label("人材の面談評価雛形")
        .max(MAX_TEXT_INPUT_VALIDATION)
        .nullable()
        .notRequired(),
    });
  }, []);

  return { matchSchema };
};
